import {Observable, of, Subject} from 'rxjs';
import {catchError, map} from 'rxjs/operators';
import {HttpBackend, HttpClient, HttpHeaders, HttpParams} from '@angular/common/http';
import {Injectable} from '@angular/core';
import {ApiConst, KindOfRequest} from './api-const';
import {AsynchronousRequest} from './asynchronous-request';
import {TranslateService} from '@ngx-translate/core';
import {CommonService} from './business/common.service';

/**
 * handle all httpRequest, if they are blocking, if they require a token...
 */
@Injectable()
export class HttpService extends HttpClient {

  /**
   * current token of the user logged
   */
  private token: string;

  private wsoToken: string;

  /**
   * the last active request on service
   */
  public activeRequest: Subject<AsynchronousRequest>;

  /**
   * the list of request that should be cancelled
   */
  public cancelledRequest: number[];

  /**
   * number of pending request as a Subject of observation
   */
  public pendingToProcess: Subject<number>;

  /**
   * number of pending request
   */
  private elementInQueue: number;

  /**
   * Title for error Message (Http)
   */
  private errorMessagetitle: string;

  /**
   * Message for error Message (Http)
   */
  private errorMessageMessage: string;
  public invalidToken: Subject<boolean>;


  /**
   *
   * instantiate variables
   *
   * @param hHttpBackend
   * @param httpClient
   * @param HttpResponse
   * @param {commonService} CommonService - handle http errors notification
   * @param {TranslateService} translate - handle translation for error notification
   */
  constructor( private hHttpBackend: HttpBackend, private httpClient: HttpClient, private commonService: CommonService, private translate: TranslateService) {
    super(hHttpBackend);
    this.getTradForService();
    this.pendingToProcess = new Subject<number>();
    this.invalidToken = new Subject<boolean>();
    this.activeRequest = new Subject<AsynchronousRequest>();
    this.cancelledRequest = [];
    this.elementInQueue = 0;
  }

  /**
   *
   * Implementation of post that handle calls using KindOfRequest
   *
   * @param {KindOfRequest} typeOfRequest - the KindOfRequest asked
   * @param {string} content - the content that you want to post
   * @param pathParams
   * @returns {Observable<any>} - the answer gave by the back-office
   */
  public $post(typeOfRequest: KindOfRequest, content: any, pathParams?: Record<string, string> ): Observable<any> {
    const currentRequest = new AsynchronousRequest(typeOfRequest.cancellable);
    this.activeRequest.next(currentRequest);

    let headers = new HttpHeaders();

    if (!typeOfRequest.uploadable) {
      headers = headers.append('Content-Type', 'application/json');
    }

    if (typeOfRequest.needToken) {
      if(this.wsoToken != null){
        headers = headers.append('X-Auth-Token', this.wsoToken);
        headers = headers.append('wso2-login', "true");
      }
      else{
        headers = headers.append('X-Auth-Token', this.token);
        headers = headers.append('wso2-login', "false");
      }

    }

    const options = {
      reportProgress: true,
      observe: 'response'
    };

    if (typeOfRequest.requestFile) {
      options['responseType'] = 'blob' as 'json';
    } else {
      options['responseType'] = 'json';
    }

    if (typeOfRequest.blockingLoading) {
      this.pendingToProcess.next(this.elementInQueue + 1);
    }
    let url = this.getFullUrl(typeOfRequest);

    if (pathParams) {
      Object.entries(pathParams).forEach(([key, value]) => {
        url = url.replace(`{${key}}`, encodeURIComponent(value));
      });
    }

    const answer = this.httpClient.post(
      url,
      content,
      { headers, observe: 'response', responseType: options['responseType'], reportProgress: options['reportProgress'] }
    );

    return answer.pipe(map((response: any ) => {
      if (typeOfRequest.blockingLoading) {
        this.pendingToProcess.next(this.elementInQueue - 1);
      }

      if (this.cancelledRequest.find(x => x === currentRequest.id)) {
        const indexOfElementToRemove = this.cancelledRequest.indexOf(currentRequest.id, 0);
        this.cancelledRequest.splice(indexOfElementToRemove, 1);
        return null;
      } else {
        const typeOfReponse = response.headers.get('content-type');

        if (typeOfReponse && typeOfReponse.includes('application/json')) {
          if (response && response.body.bTokenOk && response.body.bTokenOk === false) {
            if (response && response.body.sMsgError && response && response.body.sMsgError.indexOf('JWTVerifyException') >= 0) {
              this.commonService.addToast({
                life: 3000,
                severity: 'error',
                summary: this.translate.instant('CONFIGURATION_COMPONENT.TITLE_EXPIRED_SESSION'),
                detail: this.translate.instant('CONFIGURATION_COMPONENT.MSG_EXPIRED_SESSION')
              });
            }
            this.invalidToken.next(true);
          }
        }
        return response;
      }
    }), catchError( (response: any) => {
      this.pendingToProcess.next(this.elementInQueue - 1);
      // ignore error with applyUpdate in stach trace  (an issue in httpmodule)
      if (response && response.stack && response.stack.indexOf('applyUpdate') > 0) {
        console.log('ignored');
      }
      else {
        this.commonService.addToast({
          life: 3000,
          severity: 'error',
          summary: this.errorMessagetitle,
          detail: this.errorMessageMessage
        });
      }
      return of({});
    }));
  }
  /**
   *
   * function that give the fullUrl for a Request (that means ApiUrl+ Request Path)
   *
   * @param {KindOfRequest} typeOfRequest - the KindOfRequest for which you want the full url
   * @returns {string} - the complete url corresponding to te request
   */
  private getFullUrl(typeOfRequest: KindOfRequest): string {
    return ApiConst.apiUrl + typeOfRequest.path;
  }

  /**
   *
   * Setter for the token property
   *
   * @param {string} token - the token required for various request
   */
  public setToken(token: string): void {
    this.token = token;
  }

  public getToken(): string {
    return this.token;
  }


  public setWsoToken(token: string): void {
    this.wsoToken = token;
  }

  public getWsoToken(): string {
    return this.wsoToken;
  }

  /**
   * Handle trad for http Error
   */
  private getTradForService(): void {
    this.translate.get('TOASTER.ERROR_MESSAGE_TITLE').subscribe((translation: string) => {
      this.errorMessagetitle = translation;
    });
    this.translate.get('TOASTER.HTTP_ERROR_MESSAGE').subscribe((translation: string) => {
      this.errorMessageMessage = translation;
    });
  }

  public $get(typeOfRequest: KindOfRequest, pathParams?: Record<string, string>, queryParams?: Record<string, string>): Observable<any> {
    let headers = new HttpHeaders();

    if (typeOfRequest.needToken) {
      headers = headers.append('X-Auth-Token', this.token);
    }

    let url = this.getFullUrl(typeOfRequest);

    if (pathParams) {
      Object.entries(pathParams).forEach(([key, value]) => {
        url = url.replace(`{${key}}`, encodeURIComponent(value));
      });
    }

    let params = new HttpParams();
    if (queryParams) {
      Object.entries(queryParams).forEach(([key, value]) => {
        params = params.set(key, value);
      });
    }

    return this.httpClient.get(url, {
      headers,
      observe: 'response',
      responseType: 'blob',
      params
    });
  }

}
